/**
 * CollagePanel.tsx — Renders a collage layout using MUI ImageList.
 * Supports all four MUI ImageList variants:
 *   - `standard`  — Uniform row-based grid (default)
 *   - `quilted`   — Variable col/row spans per item for Pinterest-style quilts
 *   - `woven`     — Alternating container heights for a woven pattern
 *   - `masonry`   — CSS masonry column layout with variable-height items
 *
 * Reference: https://mui.com/material-ui/react-image-list/
 *
 * JSON trigger: `uiCompType.method: "renderCollagePanel"`
 * Data field:   `sectionData.items` — array of SectionData objects (like gridItems)
 *
 * Each item is a standard SectionData object. The component auto-detects the
 * item type and ensures proper rendering:
 *  - Items with `imageUrl`  — Rendered via `CustImage` (fill mode, object-fit cover)
 *  - Items with `text`      — Rendered via `CustText`
 *  - Items with `videoUrl`  — Rendered via `CustVideo`
 *  - Items with `uiCompType.method` — Rendered via `renderSection` as-is
 *  - Any other shape        — Delegated to `renderSection` → defaults to `Panel`
 *
 * **Layout options** (`uiCompType.options`):
 *
 * | Option          | Type     | Default      | Description |
 * |---              |---       |---           |---          |
 * | `variant`       | string   | `'standard'` | MUI ImageList variant: `standard` / `quilted` / `woven` / `masonry` |
 * | `columns`       | number   | 2            | Number of columns |
 * | `mColumns`      | number   | 1            | Override columns on mobile |
 * | `rowHeight`     | number   | 200          | Row height in pixels (for standard/quilted/woven) |
 * | `mRowHeight`    | number   | —            | Override rowHeight on mobile |
 * | `gap`           | number   | 8            | Gap in pixels between items |
 * | `objectFit`     | string   | `'cover'`    | Default `object-fit` for images |
 * | `borderRadius`  | string   | —            | Border radius applied to all items |
 *
 * **Per-item options** (on item's `uiCompType.options`):
 *  - `cols`     (number) — Column span for this item (quilted variant). Default 1.
 *  - `rows`     (number) — Row span for this item (quilted variant). Default 1.
 *
 * **Mobile overrides:** `uiCompType.mStyles` / `uiCompType.mItemStyles` override
 * desktop styles when `deviceType === 'Mobile'`.
 *
 * **Style slots:** `styles`/`mStyles` (ImageList container), `itemStyles`/`mItemStyles` (each item).
 *
 * **Title bar:** Each item object can include `itemBar` with `title`, `subtitle`,
 * `position` (`'below'` | `'top'` | `'bottom'`) to render a MUI `ImageListItemBar`.
 *
 * **Example JSON:**
 * ```json
 * {
 *   "uiCompType": { "method": "renderCollagePanel", "options": { "columns": 2, "gap": 10, "rowHeight": 200 } },
 *   "items": [
 *     { "imageUrl": "https://cdn.example.com/photo1.jpg" },
 *     { "imageUrl": "https://cdn.example.com/photo2.jpg", "link": "/category/sofas" },
 *     { "text": "(STYLE={'fontFamily':'Lora','fontSize':'28px'}) Scale Your Furniture (/STYLE)" },
 *     { "imageUrl": "https://cdn.example.com/photo3.jpg", "uiCompType": { "options": { "cols": 2 } } },
 *     { "text": "Some description text" }
 *   ]
 * }
 * ```
 */
import React, { useState, useEffect, useMemo } from "react";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import ImageListItemBar from "@mui/material/ImageListItemBar";
import { SectionData, ComponentRenderProps, renderSection } from "./index";
import { transformDescription } from "./shared";
import { useDeviceType } from "@/hooks/useDeviceType";

interface CollagePanelProps {
    sectionData: SectionData;
    props: ComponentRenderProps;
}

/**
 * Ensures a SectionData item has the right uiCompType.method and container
 * styles for proper rendering inside the MUI ImageListItem.
 *
 * - imageUrl items → method defaults to "renderCustImage" with fill container
 * - text items     → method defaults to "renderCustText" with flex centering
 * - others         → passed through unchanged to renderSection
 */
function prepareItemForRender(
    item: SectionData,
    defaultObjectFit: string,
    defaultBorderRadius?: string
): SectionData {
    // Already has an explicit method — use as-is
    if (item.uiCompType?.method) {
        return item;
    }

    // Image item: set CustImage with fill-mode container styles
    if (item.imageUrl) {
        return {
            ...item,
            uiCompType: {
                ...item.uiCompType,
                method: "renderCustImage",
                styles: {
                    objectFit: defaultObjectFit as any,
                    ...item.uiCompType?.styles,
                },
                containerStyles: {
                    position: "relative" as const,
                    width: "100%",
                    height: "100%",
                    overflow: "hidden",
                    ...(defaultBorderRadius ? { borderRadius: defaultBorderRadius } : {}),
                    ...item.uiCompType?.containerStyles,
                },
            },
        };
    }

    // Text item: set CustText
    if (item.text) {
        return {
            ...item,
            uiCompType: {
                ...item.uiCompType,
                method: "renderCustText",
            },
        };
    }

    // Everything else (panels, videos, buttons, etc.) — delegate to renderSection
    return item;
}

const CollagePanel = ({ sectionData, props }: CollagePanelProps): JSX.Element | null => {
    const deviceType = useDeviceType();
    const isMobile = deviceType === "Mobile";

    // --- Options ---
    const options = sectionData.uiCompType?.options || {};
    const variant = (options.variant || "standard") as "standard" | "quilted" | "woven" | "masonry";
    const columns = isMobile ? (options.mColumns || options.columns || 1) : (options.columns || 2);
    const gap = options.gap != null ? Number(options.gap) : 8;
    const rowHeight = isMobile ? (options.mRowHeight || options.rowHeight || 200) : (options.rowHeight || 200);
    const defaultObjectFit = options.objectFit || "cover";
    const defaultBorderRadius = options.borderRadius;

    // --- Container styles ---
    const baseStyles = useMemo(() => ({ ...sectionData.uiCompType?.styles }), [sectionData.uiCompType?.styles]);
    const mobileStyles = sectionData.uiCompType?.mStyles;
    const [containerStyles, setContainerStyles] = useState(baseStyles);

    // --- Item styles ---
    const baseItemStyles = useMemo(() => ({ ...sectionData.uiCompType?.itemStyles }), [sectionData.uiCompType?.itemStyles]);
    const mobileItemStyles = sectionData.uiCompType?.mItemStyles;
    const [itemStyles, setItemStyles] = useState(baseItemStyles);

    useEffect(() => {
        if (isMobile) {
            setContainerStyles({ ...baseStyles, ...mobileStyles });
            setItemStyles({ ...baseItemStyles, ...mobileItemStyles });
        } else {
            setContainerStyles(baseStyles);
            setItemStyles(baseItemStyles);
        }
    }, [isMobile, baseStyles, mobileStyles, baseItemStyles, mobileItemStyles]);

    // --- Parse items (must be an array of SectionData objects) ---
    const items = sectionData.items as SectionData[] | undefined;
    if (!items || !Array.isArray(items) || items.length === 0) {
        return null;
    }

    return (
        <div
            id={sectionData.trackId || "collage-panel"}
            style={{ width: "100%", ...containerStyles }}
        >
            {/* Optional title above the collage */}
            {sectionData.title && (
                <div style={{ marginBottom: `${gap}px` }}>
                    {transformDescription(
                        sectionData.title,
                        "sectionTitle",
                        sectionData.trackId,
                        undefined,
                        "h2",
                        props,
                        deviceType
                    )}
                </div>
            )}

            {/* MUI ImageList — supports standard, quilted, woven, masonry */}
            <ImageList
                variant={variant}
                cols={columns}
                rowHeight={variant === "masonry" ? undefined : rowHeight}
                gap={gap}
                sx={{
                    width: "100%",
                    margin: 0,
                    overflow: "visible",
                }}
            >
                {items.map((item: SectionData, index: number) => {
                    const perItemOptions = item.uiCompType?.options;

                    // cols/rows for quilted variant (per-item span)
                    const itemCols = isMobile
                        ? (perItemOptions?.mCols || perItemOptions?.cols || perItemOptions?.colSpan || 1)
                        : (perItemOptions?.cols || perItemOptions?.colSpan || 1);
                    const itemRows = isMobile
                        ? (perItemOptions?.mRows ?? perItemOptions?.rows ?? perItemOptions?.rowSpan ?? 1)
                        : (perItemOptions?.rows || perItemOptions?.rowSpan || 1);

                    // Item bar (optional title/subtitle overlay)
                    const itemBar = (item as any).itemBar;

                    // Prepare the item with proper defaults for CustImage/CustText
                    const prepared = prepareItemForRender(item, defaultObjectFit, defaultBorderRadius);
                    const itemKey = item.trackId || `collage-item-${index}`;

                    return (
                        <ImageListItem
                            key={itemKey}
                            cols={variant === "quilted" ? itemCols : 1}
                            rows={variant === "quilted" ? itemRows : 1}
                            sx={{
                                overflow: "hidden",
                                borderRadius: defaultBorderRadius || undefined,
                                ...itemStyles,
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "100%",
                                    height: "100%",
                                    textAlign: "center",
                                }}
                            >
                                {renderSection(itemKey, prepared, props)}
                            </div>

                            {/* Optional ImageListItemBar for titles/subtitles */}
                            {itemBar && (
                                <ImageListItemBar
                                    title={itemBar.title}
                                    subtitle={itemBar.subtitle}
                                    position={itemBar.position || "bottom"}
                                    sx={itemBar.sx}
                                />
                            )}
                        </ImageListItem>
                    );
                })}
            </ImageList>
        </div>
    );
};

export default CollagePanel;
